/**
 * ComparisonTable - Side-by-side period comparison
 *
 * Usage:
 *   <ComparisonTable
 *     title="Weekly Comparison"
 *     data={{
 *       rows: [
 *         { label: 'Steps', key: 'steps', format: 'number', higherIsBetter: true },
 *         { label: 'Sleep', key: 'sleep', format: 'number', higherIsBetter: true },
 *         { label: 'Calories', key: 'calories', format: 'number', higherIsBetter: false },
 *       ],
 *       columns: [
 *         { key: 'thisWeek', label: 'This Week' },
 *         { key: 'lastWeek', label: 'Last Week' },
 *       ],
 *       values: {
 *         steps: { thisWeek: 52000, lastWeek: 48000 },
 *         sleep: { thisWeek: 49, lastWeek: 45 },
 *         calories: { thisWeek: 14500, lastWeek: 15200 },
 *       }
 *     }}
 *   />
 */

import type { BlockProps, ComparisonTableData } from './types';
import './blocks.css';

export interface ComparisonTableProps extends BlockProps<ComparisonTableData> {
  /** Show change indicators */
  showChange?: boolean;
  /** Highlight best column */
  highlightBest?: boolean;
}

export function ComparisonTable({
  data,
  title,
  showChange = true,
  highlightBest = true,
  className,
}: ComparisonTableProps) {
  const { rows, columns, values } = data;

  if (!rows || rows.length === 0 || !columns || columns.length === 0) {
    return (
      <div className={`block-table comparison-table ${className || ''}`}>
        {title && <div className="block-table-title">{title}</div>}
        <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--color-text-faint)' }}>
          No data available
        </div>
      </div>
    );
  }

  const formatValue = (value: string | number | undefined, format?: string): string => {
    if (value === undefined || value === null) return '—';

    switch (format) {
      case 'number':
        return typeof value === 'number' ? value.toLocaleString() : String(value);
      case 'percent':
        return `${value}%`;
      case 'currency':
        return typeof value === 'number' ? `$${value.toLocaleString()}` : String(value);
      default:
        return String(value);
    }
  };

  const calculateChange = (rowKey: string): { value: number; direction: 'up' | 'down' | 'same' } | null => {
    if (columns.length < 2) return null;

    const currentVal = values[rowKey]?.[columns[0].key];
    const previousVal = values[rowKey]?.[columns[1].key];

    if (typeof currentVal !== 'number' || typeof previousVal !== 'number' || previousVal === 0) {
      return null;
    }

    const change = ((currentVal - previousVal) / previousVal) * 100;
    return {
      value: Math.abs(change),
      direction: change > 0.5 ? 'up' : change < -0.5 ? 'down' : 'same',
    };
  };

  const getBestColumn = (rowKey: string, higherIsBetter: boolean): string | null => {
    const rowValues = values[rowKey];
    if (!rowValues) return null;

    let bestKey: string | null = null;
    let bestValue: number | null = null;

    for (const col of columns) {
      const val = rowValues[col.key];
      if (typeof val === 'number') {
        if (bestValue === null ||
            (higherIsBetter && val > bestValue) ||
            (!higherIsBetter && val < bestValue)) {
          bestValue = val;
          bestKey = col.key;
        }
      }
    }

    return bestKey;
  };

  return (
    <div className={`block-table comparison-table ${className || ''}`}>
      {title && <div className="block-table-title">{title}</div>}

      <div style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', minWidth: '120px' }}>Metric</th>
              {columns.map(col => (
                <th key={col.key} style={{ textAlign: 'right', minWidth: '100px' }}>
                  {col.label}
                </th>
              ))}
              {showChange && columns.length >= 2 && (
                <th style={{ textAlign: 'right', minWidth: '80px' }}>Change</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const change = showChange ? calculateChange(row.key) : null;
              const bestColumn = highlightBest ? getBestColumn(row.key, row.higherIsBetter ?? true) : null;

              // Determine if change is "good" based on higherIsBetter
              const isChangeGood = change && row.higherIsBetter !== undefined
                ? (row.higherIsBetter && change.direction === 'up') ||
                  (!row.higherIsBetter && change.direction === 'down')
                : change?.direction === 'up';

              return (
                <tr key={row.key}>
                  <td style={{ fontWeight: 500 }}>{row.label}</td>
                  {columns.map(col => {
                    const value = values[row.key]?.[col.key];
                    const isBest = bestColumn === col.key && highlightBest;

                    return (
                      <td
                        key={col.key}
                        style={{
                          textAlign: 'right',
                          fontWeight: isBest ? 600 : 400,
                          color: isBest ? 'var(--color-success)' : 'var(--color-text)',
                        }}
                      >
                        {formatValue(value, row.format)}
                        {isBest && (
                          <span style={{ marginLeft: '4px', fontSize: '10px' }}>★</span>
                        )}
                      </td>
                    );
                  })}
                  {showChange && columns.length >= 2 && (
                    <td style={{ textAlign: 'right' }}>
                      {change ? (
                        <span style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '2px',
                          color: change.direction === 'same'
                            ? 'var(--color-text-muted)'
                            : isChangeGood
                              ? 'var(--color-success)'
                              : 'var(--color-danger)',
                          fontSize: '13px',
                          fontWeight: 500,
                        }}>
                          {change.direction === 'up' ? '↑' :
                           change.direction === 'down' ? '↓' : '→'}
                          {change.value.toFixed(1)}%
                        </span>
                      ) : (
                        <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ComparisonTable;
